import { FC, useState, useEffect } from 'react';
import { Switch } from '@heroui/react';
import { useTheme } from 'next-themes';
import clsx from 'clsx';
import { SunIcon } from '@icons/sun-icon';
import { MoonIcon } from '@icons/moon-icon';

interface ThemeSwitchClassNames {
	base?: string;
	wrapper?: string;
	thumb?: string;
}

export interface ThemeSwitchProps {
	className?: string;
	classNames?: ThemeSwitchClassNames;
	onChange?: (isDarkMode: boolean) => void;
}

export const ThemeSwitch: FC<ThemeSwitchProps> = ({ className, classNames, onChange }) => {
	const [isMounted, setIsMounted] = useState(false);
	const { theme, setTheme } = useTheme();
	const isDarkMode = theme === 'dark';

	const handleChange = (selected: boolean) => {
		setTheme(selected ? 'dark' : 'light');
		if (onChange) {
			onChange(selected);
		}
	};

	useEffect(() => {
		setIsMounted(true);
	}, []);

	// Prevent Hydration Mismatch
	if (!isMounted) {
		return <div className='w-6 h-6' />;
	}

	return (
		<Switch
			aria-label='Toggle color theme'
			isSelected={isDarkMode}
			onChange={handleChange}
			className={clsx('px-px transition-opacity hover:opacity-80', className, classNames?.base)}
		>
			<Switch.Content>
				<Switch.Control
					className={clsx(
						[
							'flex items-center justify-center',
							'rounded-lg',
							'bg-transparent',
							'!text-gray-500',
							'pt-px',
							'px-0',
							'mx-0',
							'data-[selected=true]:bg-transparent',
						],
						classNames?.wrapper,
					)}
				>
					<Switch.Thumb className={classNames?.thumb}>
						<Switch.Icon>{isDarkMode ? <MoonIcon /> : <SunIcon />}</Switch.Icon>
					</Switch.Thumb>
				</Switch.Control>
			</Switch.Content>
		</Switch>
	);
};
